import React from 'react'
import _ from 'lodash'
import Util from '@hikakaku/shared/util/util'
import {
  RANKING_STORES_PER_PAGE,
  INITIAL_SHOW_COUNT,
} from './dealer_ranking_redesign_utils'

const SORT_KEYS = {
  price: 'price',
  review: 'store.rating',
}

function initialShowCountFromProps(props) {
  const data = props.data || {}
  const perPage =
    data.initial_ranking_stores_per_page != null
      ? data.initial_ranking_stores_per_page
      : data.initialRankingStoresPerPage
  if (perPage != null) return Number(perPage)
  return INITIAL_SHOW_COUNT
}

function loadMorePageSizeFromProps(props) {
  const data = props.data || {}
  const perPage =
    data.ranking_stores_per_page != null ? data.ranking_stores_per_page : data.rankingStoresPerPage
  if (perPage != null) return Number(perPage)
  return RANKING_STORES_PER_PAGE
}

// Must match ItemsController#ranking_stores_page_offset / page_limit (after: 3 + 10/page).
function nextRankingStoresPage(loadedCount, initialPerPage, perPage) {
  if (loadedCount <= 0) return 1
  if (loadedCount < initialPerPage) return 1
  return 2 + Math.floor((loadedCount - initialPerPage) / perPage)
}

export default class DealerRankingStoresContainer extends React.Component {
  constructor(props) {
    super(props)

    this.initialShowCount = initialShowCountFromProps(props)
    this.loadMorePageSize = loadMorePageSizeFromProps(props)

    const initialPrices = props.data && props.data.prices ? props.data.prices : []
    const prPrices = props.data && props.data.pr_prices ? props.data.pr_prices : []
    const totalCount =
      props.data && props.data.total_prices_count != null
        ? props.data.total_prices_count
        : initialPrices.length

    this.state = {
      currentSortKey: '',
      maxShowCount: this.initialShowCount,
      pricesBySort: {
        recommend: initialPrices,
        price: [],
        review: [],
      },
      totalCount,
      prPrices,
      loading: false,
    }

    this.onSortChange = this.onSortChange.bind(this)
    this.onLoadMore = this.onLoadMore.bind(this)
    this.onCollapse = this.onCollapse.bind(this)
    this.fetchRankingStores = this.fetchRankingStores.bind(this)
  }

  getRankingStoresPath() {
    return this.props.ranking_stores_path || this.props.rankingStoresPath
  }

  getExcludeIds() {
    return this.props.exclude_pr_store_ids || this.props.excludePrStoreIds || []
  }

  getItemPageDesignQuery() {
    const variant = this.props.item_page_design || this.props.itemPageDesign
    if (!variant) return ''
    return `&item_page_design=${encodeURIComponent(variant)}`
  }

  nextPageForCurrentList() {
    const sort = this.state.currentSortKey || 'recommend'
    const list = this.state.pricesBySort[sort] || []
    return nextRankingStoresPage(list.length, this.initialShowCount, this.loadMorePageSize)
  }

  fetchRankingStores(sort, page) {
    const path = this.getRankingStoresPath()
    if (!path) return Promise.resolve({ prices: [], total_count: 0 })
    const sep = path.indexOf('?') >= 0 ? '&' : '?'
    const excludeIds = this.getExcludeIds()
    const excludeQuery =
      Array.isArray(excludeIds) && excludeIds.length > 0
        ? '&' + excludeIds.map((id) => `exclude_pr_store_ids[]=${encodeURIComponent(id)}`).join('&')
        : ''
    const url = `${path}${sep}sort=${sort}&page=${page}${excludeQuery}${this.getItemPageDesignQuery()}`
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((json) => ({ prices: json.prices || [], total_count: json.total_count }))
  }

  usesServerPagination() {
    return Boolean(this.getRankingStoresPath())
  }

  getCurrentList() {
    const key = this.state.currentSortKey || 'recommend'
    if (!this.usesServerPagination()) {
      const sortPath = SORT_KEYS[key]
      const raw = this.state.pricesBySort.recommend || []
      return sortPath ? Util.objectArraySort(_.cloneDeep(raw), sortPath) : raw
    }
    return this.state.pricesBySort[key] || []
  }

  ensureTabData(sortKey) {
    const key = sortKey || 'recommend'
    const path = this.getRankingStoresPath()
    if (!path) return
    const list = this.state.pricesBySort[key] || []
    if (list.length > 0 || this.state.totalCount <= 0 || this.state.loading) return
    this.setState({ loading: true })
    this.fetchRankingStores(key, 1)
      .then(({ prices, total_count }) => {
        this.setState((prev) => ({
          pricesBySort: { ...prev.pricesBySort, [key]: prices || [] },
          totalCount: total_count != null ? total_count : prev.totalCount,
          loading: false,
        }))
      })
      .catch(() => this.setState({ loading: false }))
  }

  onSortChange(e) {
    const key = e.target.value
    this.setState(
      { currentSortKey: key, maxShowCount: this.initialShowCount },
      () => {
        if (this.usesServerPagination()) this.ensureTabData(key || 'recommend')
      }
    )
  }

  onCollapse(e) {
    if (e && e.preventDefault) e.preventDefault()
    this.setState({ maxShowCount: this.initialShowCount })
  }

  onLoadMore(e) {
    if (e && e.preventDefault) e.preventDefault()

    const sort = this.state.currentSortKey || 'recommend'
    const list = this.getCurrentList()
    const total = this.state.totalCount
    const path = this.getRankingStoresPath()
    const nextShowCount = this.state.maxShowCount + this.loadMorePageSize

    // Reveal more already-fetched rows without requesting the next page.
    if (nextShowCount <= list.length) {
      this.setState({ maxShowCount: nextShowCount })
      return
    }

    if (!path || list.length >= total) {
      this.setState({ maxShowCount: Math.min(nextShowCount, list.length) })
      return
    }

    const nextPage = this.nextPageForCurrentList()
    this.setState({ maxShowCount: nextShowCount, loading: true })
    this.fetchRankingStores(sort, nextPage)
      .then(({ prices, total_count }) => {
        this.setState((prev) => {
          const currentList = prev.pricesBySort[sort] || []
          const existingIds = new Set(currentList.map((p) => p.store && p.store.id))
          const newItems = (prices || []).filter((p) => p.store && !existingIds.has(p.store.id))
          const merged = [...currentList, ...newItems]
          const maxShowCount =
            newItems.length > 0 ? prev.maxShowCount : Math.min(prev.maxShowCount, currentList.length)
          return {
            pricesBySort: { ...prev.pricesBySort, [sort]: merged },
            totalCount: total_count != null ? total_count : prev.totalCount,
            maxShowCount,
            loading: false,
          }
        })
      })
      .catch((err) => {
        console.error('ranking_stores fetch failed', err)
        this.setState({ loading: false })
      })
  }

  getViewState() {
    const list = this.getCurrentList()
    const displayList = list.slice(0, this.state.maxShowCount)
    const prCount = (this.state.prPrices || []).length
    const totalDisplayed = prCount + displayList.length
    const totalAvailable = prCount + this.state.totalCount
    const remaining = totalAvailable - totalDisplayed
    const canCollapse = this.state.maxShowCount > this.initialShowCount

    return {
      item: this.props.data.item,
      prPrices: this.state.prPrices,
      displayList,
      remaining,
      canCollapse,
      loading: this.state.loading,
      currentSortKey: this.state.currentSortKey,
      listLength: list.length,
    }
  }

  render() {
    return this.props.children({
      ...this.getViewState(),
      onSortChange: this.onSortChange,
      onLoadMore: this.onLoadMore,
      onCollapse: this.onCollapse,
    })
  }
}
